import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { AdminPageHeader } from './AdminLayout';
import { Loader2, Save, ArrowLeft, ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';

export function AdminPagesList() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/admin/pages').then((r) => setPages(r.data || [])).finally(() => setLoading(false));
  }, []);
  return (
    <div>
      <AdminPageHeader title="Pages (CMS)" description="Edit your public pages. Each field is editable per language." />
      {loading ? <Loader2 className="animate-spin" /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pages.map((p) => (
            <Link key={p.key} to={`/admin/pages/${p.key}`} className="rounded-3xl bg-white border border-black/10 p-6 lift-card">
              <p className="text-xs uppercase tracking-widest font-semibold text-stone-500">{p.key}</p>
              <p className="heading-display text-2xl mt-2">{p.name}</p>
              <p className="mt-3 text-sm text-stone-600">{Object.keys(p.content || {}).length} languages · {Object.keys(p.content?.en || {}).length} fields</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function AdminPageEditor() {
  const { key } = useParams();
  const nav = useNavigate();
  const [page, setPage] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    Promise.all([
      api.get(`/admin/pages/${key}`).then((r) => r.data),
      api.get('/admin/languages').then((r) => r.data || []),
    ]).then(([p, langs]) => {
      setPage(p);
      setLanguages(langs.filter(l => l.active));
      const def = langs.find(l => l.is_default) || langs[0];
      setOpen({ [def?.code || 'en']: true });
    });
  }, [key]);

  if (!page) return <Loader2 className="animate-spin" />;

  const updateField = (lang, slot, value) => {
    setPage((p) => ({ ...p, content: { ...p.content, [lang]: { ...(p.content[lang] || {}), [slot]: value } } }));
  };
  const updateSeo = (lang, slot, value) => {
    setPage((p) => ({ ...p, seo: { ...p.seo, [lang]: { ...(p.seo[lang] || {}), [slot]: value } } }));
  };
  const addField = (lang) => {
    const slot = window.prompt('Field key (e.g. hero_title):');
    if (!slot) return;
    updateField(lang, slot, '');
  };
  const removeField = (lang, slot) => {
    if (!window.confirm(`Remove field "${slot}" from ${lang}?`)) return;
    setPage((p) => {
      const c = { ...(p.content[lang] || {}) };
      delete c[slot];
      return { ...p, content: { ...p.content, [lang]: c } };
    });
  };
  const save = async () => {
    setSaving(true);
    setMsg('');
    try {
      await api.put(`/admin/pages/${key}`, { key: page.key, name: page.name, seo: page.seo, content: page.content });
      setMsg('Saved successfully.');
    } catch (e) {
      setMsg('Failed to save.');
    } finally { setSaving(false); }
  };

  // Get all unique slot keys across languages
  const allSlots = (lang) => Object.keys(page.content[lang] || {});
  const seoFields = ['title', 'description', 'keywords', 'og_image'];

  return (
    <div>
      <Link to="/admin/pages" className="inline-flex items-center gap-2 text-sm text-stone-600 hover:text-[#0F0F10] mb-4"><ArrowLeft size={14} /> All pages</Link>
      <AdminPageHeader
        title={page.name}
        description={`Page key: ${page.key}`}
        actions={(
          <div className="flex items-center gap-3">
            {msg && <span className="text-sm text-stone-600">{msg}</span>}
            <button onClick={save} disabled={saving} className="btn-pill">
              {saving ? <Loader2 className="animate-spin" size={16} /> : <><Save size={16} /><span>Save</span></>}
            </button>
          </div>
        )}
      />
      <label className="block max-w-md mb-6">
        <span className="text-sm font-medium text-stone-700">Page name (admin)</span>
        <input value={page.name} onChange={(e) => setPage({ ...page, name: e.target.value })}
          className="mt-2 w-full rounded-2xl border border-black/15 bg-white px-4 py-2.5 text-[15px]" />
      </label>

      <div className="space-y-3">
        {languages.map((l) => {
          const isOpen = !!open[l.code];
          return (
            <div key={l.code} className="rounded-2xl bg-white border border-black/10 overflow-hidden">
              <button onClick={() => setOpen((s) => ({ ...s, [l.code]: !isOpen }))} className="w-full flex items-center justify-between px-5 py-4 hover:bg-stone-50">
                <span className="flex items-center gap-3">
                  <span className="text-xs uppercase tracking-widest font-bold bg-[#F4E94B] px-2 py-1 rounded">{l.code}</span>
                  <span className="font-semibold">{l.native_name}</span>
                  {l.is_default && <span className="text-xs bg-[#1E2DFF] text-white px-2 py-0.5 rounded">default</span>}
                </span>
                {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
              </button>
              {isOpen && (
                <div className="px-5 pb-6 space-y-5">
                  <div>
                    <p className="text-xs uppercase tracking-widest font-semibold text-stone-500 mb-3">SEO</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {seoFields.map((s) => (
                        <label key={s} className="block">
                          <span className="text-xs text-stone-600 font-medium">{s}</span>
                          <input value={(page.seo[l.code] || {})[s] || ''} onChange={(e) => updateSeo(l.code, s, e.target.value)}
                            className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm" />
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs uppercase tracking-widest font-semibold text-stone-500">Content fields</p>
                      <button onClick={() => addField(l.code)} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border border-black/15 hover:bg-stone-50"><Plus size={14} /> Add field</button>
                    </div>
                    <div className="space-y-2">
                      {allSlots(l.code).map((slot) => {
                        const val = (page.content[l.code] || {})[slot] || '';
                        const isLong = val.length > 80 || /title|desc|description|text|message|sections|intro/.test(slot);
                        return (
                          <div key={slot} className="flex items-start gap-2">
                            <div className="flex-1">
                              <label className="block">
                                <span className="text-xs text-stone-600 font-mono">{slot}</span>
                                {isLong ? (
                                  <textarea rows={Math.max(2, Math.ceil((val.length || 1) / 80))} value={val} onChange={(e) => updateField(l.code, slot, e.target.value)}
                                    className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm" />
                                ) : (
                                  <input value={val} onChange={(e) => updateField(l.code, slot, e.target.value)}
                                    className="mt-1 w-full rounded-xl border border-black/15 bg-white px-3 py-2 text-sm" />
                                )}
                              </label>
                            </div>
                            <button onClick={() => removeField(l.code, slot)} className="mt-6 p-2 text-stone-400 hover:text-red-600"><Trash2 size={14} /></button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
